import { useEffect } from 'react';

import { addBreadcrumb } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';

export type BrandingResourceKey = 'company-logo' | 'company-logo-dark' | 'co-brand-css';

// Pre-signed URLs last an hour, so a fresh one is fetched well before the one in use expires
const URL_REFRESH_MS = 45 * 60 * 1000;

/**
 * A pre-signed URL for one of the company's branding files, or null when it has none. No toast
 * on error: an unbranded portal is the fallback, so a breadcrumb is left for whatever follows.
 */
const useFetchBrandingResource = (resourceKey: BrandingResourceKey, companyId?: string) => {
  const query = useQuery({
    queryKey: ['fetchBrandingResource', companyId, resourceKey],
    queryFn: () => FileProcessingApiHandler.getResourceDownloadPreSignedUrl(resourceKey),
    enabled: !!companyId,
    staleTime: URL_REFRESH_MS,
    // The provider never remounts, so without an interval the first URL would be used past its expiry
    refetchInterval: URL_REFRESH_MS,
    refetchIntervalInBackground: true,
  });

  const { isError } = query;

  useEffect(() => {
    if (isError) {
      addBreadcrumb({
        category: 'branding',
        level: 'warning',
        message: 'Company branding failed to load; showing the Aslan look',
        data: { companyId, resourceKey },
      });
    }
  }, [isError, companyId, resourceKey]);

  return query;
};

export default useFetchBrandingResource;
